import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { portalApi } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import {
  Package, Truck, MessageSquare, User, ArrowRight, Check, Loader2, LogOut,
  Building2, MapPin, FileUp, Landmark, Users, ChevronRight, Send, AlertCircle,
  Lock, Phone, Plus, Wallet, Download, FileText,
} from 'lucide-react';
import { cn } from '../lib/utils';
import { toast, Toaster } from 'sonner';
import axios from 'axios';

const STEPS = [
  { num: 1, label: 'Company', icon: Building2 },
  { num: 2, label: 'Address', icon: MapPin },
  { num: 3, label: 'Documents', icon: FileUp },
  { num: 4, label: 'Banking', icon: Landmark },
  { num: 5, label: 'Contacts', icon: Users },
];

const ORG_TYPES = ["Proprietorship", "LLP", "Partnership", "Private Limited", "Public Limited", "HUF", "Trust", "Other"];

const QUERY_CATEGORIES = ["Finance — Ledger Request", "Finance — Invoice Copy", "Finance — Credit Note", "Finance — Debit Note", "Finance — Other", "Product Catalogue Request", "Order Enquiry", "General Query"];

const Portal = () => {
  const [searchParams] = useSearchParams();
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('orders');
  const [kycStep, setKycStep] = useState(1);
  const [kyc, setKyc] = useState({});
  const [savingKyc, setSavingKyc] = useState(false);
  const [orders, setOrders] = useState([]);
  const [dispatches, setDispatches] = useState([]);
  const [queries, setQueries] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [queryDialog, setQueryDialog] = useState(false);
  const [queryForm, setQueryForm] = useState({ category: '', subject: '', message: '' });
  const [submittingQuery, setSubmittingQuery] = useState(false);
  const [replyDialog, setReplyDialog] = useState(null);
  const [replyMsg, setReplyMsg] = useState('');
  const [billingIsSameAsShipping, setBillingIsSameAsShipping] = useState(true);
  // Linked / sister companies
  const [linkedCompanies, setLinkedCompanies] = useState([]);
  const [switching, setSwitching] = useState(false);
  // Ledger
  const [ledger, setLedger] = useState(null);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [ledgerFrom, setLedgerFrom] = useState('');
  const [ledgerTo, setLedgerTo] = useState('');
  const [downloadingLedger, setDownloadingLedger] = useState(false);

  // Auth
  useEffect(() => {
    const token = searchParams.get('token');
    const requestedTab = searchParams.get('tab');
    const savedToken = localStorage.getItem('siddhi_customer_token');
    if (token) {
      portalApi.auth(token).then(res => {
        localStorage.setItem('siddhi_customer_token', res.data.access_token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${res.data.access_token}`;
        setCustomer(res.data.customer);
        if (requestedTab) setTab(requestedTab);
        else if (res.data.customer.kyc_status === 'pending') setTab('kyc');
        setLoading(false);
      }).catch(() => { toast.error('Invalid or expired link'); setLoading(false); });
    } else if (savedToken) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${savedToken}`;
      portalApi.me().then(res => {
        setCustomer(res.data);
        if (requestedTab) setTab(requestedTab);
        else if (res.data.kyc_status === 'pending') setTab('kyc');
        setLoading(false);
      }).catch(() => { localStorage.removeItem('siddhi_customer_token'); setLoading(false); });
    } else {
      setLoading(false);
    }
  }, [searchParams]);

  // Fetch linked companies whenever the customer changes
  useEffect(() => {
    if (!customer) { setLinkedCompanies([]); return; }
    portalApi.listLinkedCompanies()
      .then(r => setLinkedCompanies(r.data || []))
      .catch(() => setLinkedCompanies([]));
  }, [customer?.id]);

  const handleSwitchCompany = async (targetId) => {
    if (targetId === customer?.id) return;
    setSwitching(true);
    try {
      const r = await portalApi.switchCompany(targetId);
      localStorage.setItem('siddhi_customer_token', r.data.access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${r.data.access_token}`;
      setCustomer(r.data.customer);
      // Reset to orders tab so the user lands somewhere meaningful for the switched company
      setTab('orders');
      toast.success(`Switched to ${r.data.customer.company_name}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not switch company');
    } finally {
      setSwitching(false);
    }
  };

  // Fetch data when tab changes
  useEffect(() => {
    if (!customer) return;
    if (tab === 'orders') portalApi.getOrders().then(r => setOrders(r.data)).catch(() => {});
    if (tab === 'dispatches') portalApi.getDispatches().then(r => setDispatches(r.data)).catch(() => {});
    if (tab === 'queries') portalApi.getQueries().then(r => setQueries(r.data)).catch(() => {});
    if (tab === 'kyc') portalApi.getKYC().then(r => { setKyc(r.data.kyc || {}); setKycStep(Math.max(1, r.data.kyc_step || 0)); }).catch(() => {});
    if (tab === 'ledger') {
      setLedgerLoading(true);
      const params = {};
      if (ledgerFrom) params.from_date = ledgerFrom;
      if (ledgerTo) params.to_date = ledgerTo;
      portalApi.getLedger(params)
        .then(r => setLedger(r.data))
        .catch(() => toast.error('Could not load ledger'))
        .finally(() => setLedgerLoading(false));
    }
  }, [tab, customer, ledgerFrom, ledgerTo]);

  const handleSaveKycStep = async (stepNum, data) => {
    setSavingKyc(true);
    try {
      await portalApi.saveKYC(stepNum, data);
      setKyc(prev => ({ ...prev, [`step_${stepNum}`]: data }));
      toast.success(`Step ${stepNum} saved`);
      if (stepNum < 5) setKycStep(stepNum + 1);
      else { toast.success('KYC complete! Welcome to Siddhi Jewels.'); setTab('orders'); setCustomer(p => ({ ...p, kyc_status: 'complete' })); }
    } catch { toast.error('Failed to save'); }
    finally { setSavingKyc(false); }
  };

  const handleSubmitQuery = async () => {
    if (!queryForm.category || !queryForm.subject || !queryForm.message) { toast.error('Fill all fields'); return; }
    setSubmittingQuery(true);
    try {
      await portalApi.createQuery(queryForm);
      toast.success('Query submitted');
      setQueryDialog(false);
      setQueryForm({ category: '', subject: '', message: '' });
      portalApi.getQueries().then(r => setQueries(r.data));
    } catch { toast.error('Failed to submit'); }
    finally { setSubmittingQuery(false); }
  };

  const handleReply = async (queryId) => {
    if (!replyMsg.trim()) return;
    try {
      await portalApi.replyQuery(queryId, replyMsg);
      setReplyMsg('');
      setReplyDialog(null);
      portalApi.getQueries().then(r => setQueries(r.data));
      toast.success('Reply sent');
    } catch { toast.error('Failed'); }
  };

  const logout = () => { localStorage.removeItem('siddhi_customer_token'); delete axios.defaults.headers.common['Authorization']; setCustomer(null); };

  const fmtDate = (d) => { if (!d) return '—'; try { return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return '—'; } };

  if (loading) return (
    <div className="min-h-screen bg-white dark:bg-neutral-950 flex items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
    </div>
  );

  // Not logged in
  if (!customer) return (
    <div className="min-h-screen bg-white dark:bg-neutral-950 flex items-center justify-center p-6">
      <Toaster position="top-center" richColors />
      <div className="w-full max-w-md text-center space-y-8">
        <div className="h-20 w-20 mx-auto rounded-3xl bg-amber-600 flex items-center justify-center">
          <Building2 className="h-10 w-10 text-white" strokeWidth={1.5} />
        </div>
        <div className="space-y-3">
          <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight">Siddhi Jewels</h1>
          <p className="text-lg text-muted-foreground">Customer Portal</p>
        </div>
        <div className="space-y-4 pt-4">
          <p className="text-base leading-relaxed text-muted-foreground">
            Please use the magic link sent to your WhatsApp to access your portal.
          </p>
          <p className="text-sm text-muted-foreground/70 leading-relaxed">
            Don't have a link? Message our WhatsApp bot or contact your Siddhi Jewels representative.
          </p>
        </div>
      </div>
    </div>
  );

  const TABS = [
    { key: 'orders', label: 'Orders', icon: Package },
    { key: 'dispatches', label: 'Dispatches', icon: Truck },
    { key: 'ledger', label: 'Ledger', icon: Wallet },
    { key: 'queries', label: 'Queries', icon: MessageSquare },
    { key: 'kyc', label: 'Profile', icon: User },
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-neutral-950">
      <Toaster position="top-center" richColors />

      {/* Header */}
      <header className="sticky top-0 z-30 bg-white/90 dark:bg-neutral-950/90 backdrop-blur border-b border-neutral-200/60 dark:border-neutral-800">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 py-4 sm:py-5 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 sm:gap-4 min-w-0">
            <div className="h-10 w-10 sm:h-11 sm:w-11 rounded-2xl bg-amber-600 flex items-center justify-center flex-shrink-0">
              <Building2 className="h-5 w-5 text-white" strokeWidth={2} />
            </div>
            <div className="min-w-0">
              <p className="text-sm sm:text-base font-semibold tracking-tight">Siddhi Jewels</p>
              {linkedCompanies.length > 0 ? (
                <Select
                  value={customer.id}
                  onValueChange={handleSwitchCompany}
                  disabled={switching}
                >
                  <SelectTrigger className="h-7 px-2 text-xs sm:text-sm border-0 bg-transparent hover:bg-muted/40 -ml-1 max-w-[220px]" data-testid="company-switcher">
                    <SelectValue>{customer.company_name}</SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={customer.id} className="text-sm font-semibold">{customer.company_name}</SelectItem>
                    {linkedCompanies.map(lc => (
                      <SelectItem key={lc.id} value={lc.id} className="text-sm" data-testid={`switch-to-${lc.id}`}>{lc.company_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <p className="text-xs sm:text-sm text-muted-foreground truncate">{customer.company_name}</p>
              )}
            </div>
          </div>
          <Button variant="ghost" onClick={logout} className="text-sm text-muted-foreground hover:text-foreground px-2 sm:px-3" data-testid="portal-logout">
            <LogOut className="h-4 w-4 sm:mr-1.5" />
            <span className="hidden sm:inline">Logout</span>
          </Button>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-6 sm:py-10">
        {/* KYC Banner */}
        {customer.kyc_status === 'changes_requested' && (
          <div className="mb-8 p-5 rounded-2xl bg-red-50 dark:bg-red-500/10 border border-red-200/60 dark:border-red-500/20">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1 space-y-2">
                <p className="text-base font-semibold text-red-900 dark:text-red-300">KYC changes requested</p>
                {customer.kyc_change_message && <p className="text-sm text-red-700 dark:text-red-400 leading-relaxed">{customer.kyc_change_message}</p>}
                <Button size="sm" className="mt-2 bg-red-600 hover:bg-red-700 text-white" onClick={() => setTab('kyc')}>Update KYC</Button>
              </div>
            </div>
          </div>
        )}
        {customer.kyc_status !== 'complete' && customer.kyc_status !== 'approved' && customer.kyc_status !== 'changes_requested' && tab !== 'kyc' && (
          <div className="mb-8 p-5 rounded-2xl bg-amber-50 dark:bg-amber-500/10 border border-amber-200/60 dark:border-amber-500/20 flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3 min-w-0">
              <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0" />
              <p className="text-base text-amber-900 dark:text-amber-300">Complete your profile to unlock all features</p>
            </div>
            <Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-white" onClick={() => setTab('kyc')}>Complete</Button>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-10 overflow-x-auto -mx-2 px-2 pb-2">
          {TABS.map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={cn(
                "flex items-center gap-2 px-5 py-2.5 rounded-full text-base font-medium transition-all whitespace-nowrap",
                tab === t.key
                  ? "bg-neutral-900 text-white dark:bg-white dark:text-neutral-900"
                  : "text-muted-foreground hover:bg-neutral-100 dark:hover:bg-neutral-800"
              )}
              data-testid={`tab-${t.key}`}
            >
              <t.icon className="h-4 w-4" />{t.label}
            </button>
          ))}
        </div>

        {/* ═══ ORDERS TAB ═══ */}
        {tab === 'orders' && (
          <div className="space-y-6">
            <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight">Your orders</h2>
            {orders.length === 0 ? (
              <div className="text-center py-24">
                <Package className="h-10 w-10 mx-auto mb-4 text-muted-foreground/30" strokeWidth={1.5} />
                <p className="text-lg text-muted-foreground">No orders yet</p>
              </div>
            ) : (
              <div className="divide-y divide-neutral-200/60 dark:divide-neutral-800">
                {orders.map(o => (
                  <button
                    key={o.id}
                    onClick={() => setSelectedOrder(o)}
                    className="w-full text-left py-6 flex items-start gap-5 group hover:bg-neutral-50 dark:hover:bg-neutral-900/50 -mx-4 px-4 rounded-xl transition-colors"
                    data-testid={`order-${o.id}`}
                  >
                    <div className="flex-1 min-w-0 space-y-2">
                      <div className="flex items-baseline gap-3 flex-wrap">
                        <p className="text-xl font-semibold tracking-tight">{o.order_number}</p>
                        <span className={cn(
                          "text-xs font-medium px-2.5 py-1 rounded-full",
                          o.status === 'Dispatched'
                            ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400"
                            : "bg-neutral-100 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300"
                        )}>
                          {o.status}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 text-base text-muted-foreground flex-wrap">
                        <span>{o.pieces} pieces</span>
                        <span className="text-muted-foreground/40">·</span>
                        <span>{o.dispatched} dispatched</span>
                        {o.delivery_date && (
                          <>
                            <span className="text-muted-foreground/40">·</span>
                            <span>Due {fmtDate(o.delivery_date)}</span>
                          </>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground/70">Placed {fmtDate(o.created_at)}</p>
                      {o.stages && o.stages.length > 0 && (
                        <div className="flex flex-wrap gap-2 pt-2">
                          {o.stages.map((s, i) => (
                            <span key={i} className="text-sm px-3 py-1 rounded-full bg-amber-50 dark:bg-amber-500/10 text-amber-800 dark:text-amber-400 border border-amber-200/50 dark:border-amber-500/20">
                              {s.pieces} in {s.stage}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <ChevronRight className="h-5 w-5 text-muted-foreground/30 mt-2 group-hover:text-muted-foreground transition-colors flex-shrink-0" />
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ═══ DISPATCHES TAB ═══ */}
        {tab === 'dispatches' && (
          <div className="space-y-6">
            <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight">Dispatch history</h2>
            {dispatches.length === 0 ? (
              <div className="text-center py-24">
                <Truck className="h-10 w-10 mx-auto mb-4 text-muted-foreground/30" strokeWidth={1.5} />
                <p className="text-lg text-muted-foreground">No dispatches yet</p>
              </div>
            ) : (
              <div className="divide-y divide-neutral-200/60 dark:divide-neutral-800">
                {dispatches.map((d, i) => (
                  <div key={i} className="py-6 space-y-3" data-testid={`dispatch-${i}`}>
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div className="space-y-1">
                        <p className="text-xl font-semibold tracking-tight">{d.order_number}</p>
                        <p className="text-base text-muted-foreground">{d.pieces} pieces dispatched</p>
                      </div>
                      <p className="text-base text-muted-foreground whitespace-nowrap">{fmtDate(d.date)}</p>
                    </div>
                    <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-muted-foreground">
                      {d.ref && <span><span className="text-muted-foreground/60">Ref</span> {d.ref}</span>}
                      {d.logistics && <span><span className="text-muted-foreground/60">Via</span> {d.logistics}</span>}
                      {d.tracking && <span><span className="text-muted-foreground/60">Tracking</span> {d.tracking}</span>}
                    </div>
                    {d.tracking && d.logistics?.toLowerCase().includes('sequel') && (
                      <a
                        href={`https://sequel247.com/track/${d.tracking}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 mt-1 px-4 py-2 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium transition-colors"
                        data-testid={`track-btn-${i}`}
                      >
                        <Truck className="h-4 w-4" /> Track shipment
                        <ArrowRight className="h-4 w-4" />
                      </a>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ═══ LEDGER TAB ═══ */}
        {tab === 'ledger' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div>
                <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight">Your ledger</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Statement of account — invoices, payments and adjustments with running balance.
                </p>
              </div>
              <Button
                onClick={async () => {
                  setDownloadingLedger(true);
                  try {
                    const params = {};
                    if (ledgerFrom) params.from_date = ledgerFrom;
                    if (ledgerTo) params.to_date = ledgerTo;
                    const qs = new URLSearchParams(params).toString();
                    const token = localStorage.getItem('siddhi_customer_token');
                    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/customer-portal/ledger.pdf${qs ? '?' + qs : ''}`, {
                      headers: { Authorization: `Bearer ${token}` }
                    });
                    if (!res.ok) throw new Error();
                    const blob = await res.blob();
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `Ledger-${customer.company_name || 'me'}.pdf`;
                    document.body.appendChild(a); a.click(); a.remove();
                    URL.revokeObjectURL(url);
                  } catch { toast.error('PDF download failed'); }
                  finally { setDownloadingLedger(false); }
                }}
                disabled={downloadingLedger || !ledger}
                className="bg-violet-600 hover:bg-violet-700 text-white rounded-full"
                data-testid="portal-download-ledger-pdf"
              >
                {downloadingLedger ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Download className="h-4 w-4 mr-2" />}
                Download PDF
              </Button>
            </div>

            {/* Date filters */}
            <div className="flex flex-wrap gap-3 items-end">
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">From</Label>
                <Input type="date" value={ledgerFrom} onChange={e => setLedgerFrom(e.target.value)}
                  className="h-10 w-44" data-testid="portal-ledger-from" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">To</Label>
                <Input type="date" value={ledgerTo} onChange={e => setLedgerTo(e.target.value)}
                  className="h-10 w-44" data-testid="portal-ledger-to" />
              </div>
              {(ledgerFrom || ledgerTo) && (
                <Button variant="ghost" className="h-10 text-sm" onClick={() => { setLedgerFrom(''); setLedgerTo(''); }}>
                  Clear
                </Button>
              )}
            </div>

            {ledgerLoading ? (
              <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-violet-600" /></div>
            ) : !ledger ? (
              <div className="text-center py-24">
                <FileText className="h-10 w-10 mx-auto mb-4 text-muted-foreground/30" strokeWidth={1.5} />
                <p className="text-lg text-muted-foreground">No transactions yet</p>
              </div>
            ) : (
              <>
                {/* Summary */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[
                    { label: 'Opening', value: ledger.opening_balance, muted: true },
                    { label: 'Total debits', value: ledger.total_debit, color: 'text-rose-600' },
                    { label: 'Total credits', value: ledger.total_credit, color: 'text-emerald-600' },
                    { label: 'Closing', value: ledger.closing_balance, strong: true,
                      color: ledger.closing_balance > 0 ? 'text-rose-600' : 'text-emerald-600' },
                  ].map((s, i) => (
                    <div key={i} className={cn('p-4 rounded-2xl border', s.muted ? 'bg-slate-50 dark:bg-neutral-900' : 'bg-white dark:bg-neutral-900')}>
                      <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{s.label}</p>
                      <p className={cn('mt-1 tabular-nums', s.strong ? 'text-xl font-bold' : 'text-base font-semibold', s.color)}>
                        ₹{(s.value || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </p>
                    </div>
                  ))}
                </div>

                {/* Transactions */}
                <div className="border rounded-2xl overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="portal-ledger-table">
                      <thead className="bg-slate-50 dark:bg-neutral-800/50 text-muted-foreground">
                        <tr>
                          <th className="text-left px-4 py-3 font-semibold uppercase tracking-wider text-[10px]">Date</th>
                          <th className="text-left px-4 py-3 font-semibold uppercase tracking-wider text-[10px]">Particulars</th>
                          <th className="text-left px-4 py-3 font-semibold uppercase tracking-wider text-[10px] hidden sm:table-cell">Ref</th>
                          <th className="text-right px-4 py-3 font-semibold uppercase tracking-wider text-[10px]">Debit</th>
                          <th className="text-right px-4 py-3 font-semibold uppercase tracking-wider text-[10px]">Credit</th>
                          <th className="text-right px-4 py-3 font-semibold uppercase tracking-wider text-[10px]">Balance</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ledger.events.length === 0 ? (
                          <tr><td colSpan={6} className="text-center py-10 text-muted-foreground">No transactions in this period.</td></tr>
                        ) : ledger.events.map((ev, i) => (
                          <tr key={i} className="border-t">
                            <td className="px-4 py-2 whitespace-nowrap font-mono text-xs">
                              {new Date(ev.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                            </td>
                            <td className="px-4 py-2">
                              <div>{ev.label}</div>
                              {ev.description && <div className="text-[10px] text-muted-foreground italic mt-0.5">{ev.description}</div>}
                            </td>
                            <td className="px-4 py-2 text-muted-foreground hidden sm:table-cell">{ev.ref || ev.co_number || '—'}</td>
                            <td className="px-4 py-2 text-right tabular-nums text-rose-600">{ev.debit > 0 ? `\u20B9${ev.debit.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : ''}</td>
                            <td className="px-4 py-2 text-right tabular-nums text-emerald-600">{ev.credit > 0 ? `\u20B9${ev.credit.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : ''}</td>
                            <td className={cn("px-4 py-2 text-right tabular-nums font-bold",
                              ev.balance > 0 ? 'text-slate-900 dark:text-slate-100' : 'text-emerald-700')}>
                              ₹{ev.balance.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* ═══ QUERIES TAB ═══ */}
        {tab === 'queries' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight">Your queries</h2>
              <Button
                onClick={() => setQueryDialog(true)}
                className="h-11 px-5 rounded-full bg-amber-600 hover:bg-amber-700 text-white text-base"
                data-testid="raise-query-btn"
              >
                <Send className="h-4 w-4 mr-2" /> Raise query
              </Button>
            </div>
            {queries.length === 0 ? (
              <div className="text-center py-24">
                <MessageSquare className="h-10 w-10 mx-auto mb-4 text-muted-foreground/30" strokeWidth={1.5} />
                <p className="text-lg text-muted-foreground">No queries yet</p>
              </div>
            ) : (
              <div className="divide-y divide-neutral-200/60 dark:divide-neutral-800">
                {queries.map(q => (
                  <button
                    key={q.id}
                    onClick={() => setReplyDialog(q)}
                    className="w-full text-left py-6 flex items-start gap-5 group hover:bg-neutral-50 dark:hover:bg-neutral-900/50 -mx-4 px-4 rounded-xl transition-colors"
                    data-testid={`query-${q.id}`}
                  >
                    <div className="flex-1 min-w-0 space-y-2">
                      <div className="flex items-baseline gap-3 flex-wrap">
                        <p className="text-xl font-semibold tracking-tight">{q.subject}</p>
                        <span className={cn(
                          "text-xs font-medium px-2.5 py-1 rounded-full",
                          q.status === 'open' ? "bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-400" :
                          q.status === 'responded' ? "bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-400" :
                          "bg-neutral-100 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300"
                        )}>
                          {q.status}
                        </span>
                      </div>
                      <p className="text-base text-muted-foreground">{q.category}</p>
                      <p className="text-sm text-muted-foreground/70">{q.messages?.length || 0} messages · updated {fmtDate(q.updated_at)}</p>
                    </div>
                    <ChevronRight className="h-5 w-5 text-muted-foreground/30 mt-2 group-hover:text-muted-foreground transition-colors flex-shrink-0" />
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ═══ KYC TAB ═══ */}
        {tab === 'kyc' && (
          <div className="space-y-8">
            <div className="space-y-3">
              <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight">Complete your profile</h2>
              <p className="text-lg text-muted-foreground">Takes about 5 minutes. We'll keep your data safe.</p>
            </div>

            {/* Progress */}
            <div className="flex items-center gap-2 overflow-x-auto -mx-2 px-2 pb-2">
              {STEPS.map((s, i) => (
                <React.Fragment key={s.num}>
                  <button
                    onClick={() => setKycStep(s.num)}
                    className={cn(
                      "flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap",
                      kycStep === s.num ? "bg-neutral-900 text-white dark:bg-white dark:text-neutral-900" :
                      kyc[`step_${s.num}`] ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400" :
                      "bg-neutral-100 text-muted-foreground dark:bg-neutral-800 hover:bg-neutral-200 dark:hover:bg-neutral-700"
                    )}
                    data-testid={`kyc-step-${s.num}`}
                  >
                    {kyc[`step_${s.num}`] ? <Check className="h-4 w-4" /> : <s.icon className="h-4 w-4" />}
                    <span>{s.label}</span>
                  </button>
                  {i < STEPS.length - 1 && <div className={cn("h-px w-4 flex-shrink-0", kyc[`step_${s.num}`] ? "bg-emerald-400" : "bg-neutral-200 dark:bg-neutral-800")} />}
                </React.Fragment>
              ))}
            </div>

            <div className="pt-2">
              <KYCStepForm step={kycStep} kyc={kyc} customer={customer} saving={savingKyc} onSave={handleSaveKycStep} onContactAdded={() => portalApi.me().then(r => setCustomer(r.data))} billingIsSameAsShipping={billingIsSameAsShipping} setBillingIsSameAsShipping={setBillingIsSameAsShipping} />
            </div>
          </div>
        )}
      </div>

      {/* Order Detail Dialog */}
      <Dialog open={!!selectedOrder} onOpenChange={() => setSelectedOrder(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader className="space-y-2">
            <DialogTitle className="text-2xl font-semibold tracking-tight">{selectedOrder?.order_number}</DialogTitle>
            <DialogDescription className="text-base">{selectedOrder?.status} · {selectedOrder?.pieces} pieces</DialogDescription>
          </DialogHeader>
          {selectedOrder?.stages?.length > 0 && (
            <div className="space-y-3 py-4">
              <p className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Stage breakdown</p>
              <div className="divide-y divide-neutral-200/60 dark:divide-neutral-800">
                {selectedOrder.stages.map((s, i) => (
                  <div key={i} className="flex items-center justify-between py-3">
                    <span className="text-base">{s.stage}</span>
                    <span className="text-base font-semibold tabular-nums">{s.pieces} pcs</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Query Dialog */}
      <Dialog open={queryDialog} onOpenChange={setQueryDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-2xl font-semibold tracking-tight">Raise a query</DialogTitle>
            <DialogDescription className="text-base">We'll get back to you as soon as possible.</DialogDescription>
          </DialogHeader>
          <div className="space-y-5 py-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium">Category</Label>
              <Select value={queryForm.category} onValueChange={v => setQueryForm(p => ({ ...p, category: v }))}>
                <SelectTrigger className="h-12 text-base" data-testid="query-category"><SelectValue placeholder="Select category..." /></SelectTrigger>
                <SelectContent>{QUERY_CATEGORIES.map(c => <SelectItem key={c} value={c} className="text-base">{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium">Subject</Label>
              <Input value={queryForm.subject} onChange={e => setQueryForm(p => ({ ...p, subject: e.target.value }))} className="h-12 text-base" data-testid="query-subject" />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium">Message</Label>
              <Textarea value={queryForm.message} onChange={e => setQueryForm(p => ({ ...p, message: e.target.value }))} rows={4} className="text-base resize-none" data-testid="query-message" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setQueryDialog(false)} className="h-11">Cancel</Button>
            <Button onClick={handleSubmitQuery} disabled={submittingQuery} className="h-11 bg-amber-600 hover:bg-amber-700 text-white" data-testid="submit-query-btn">
              {submittingQuery ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />} Submit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reply Dialog */}
      <Dialog open={!!replyDialog} onOpenChange={() => setReplyDialog(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader className="space-y-2">
            <DialogTitle className="text-2xl font-semibold tracking-tight">{replyDialog?.subject}</DialogTitle>
            <DialogDescription className="text-base">{replyDialog?.category}</DialogDescription>
          </DialogHeader>
          <ScrollArea className="max-h-[320px] pr-2">
            <div className="space-y-3 py-2">
              {replyDialog?.messages?.map((m, i) => (
                <div key={i} className={cn("p-4 rounded-2xl", m.sender === 'customer' ? "bg-amber-50 dark:bg-amber-500/10 ml-8" : "bg-neutral-100 dark:bg-neutral-800 mr-8")}>
                  <p className="text-xs font-medium text-muted-foreground mb-1.5">{m.sender_name} · {fmtDate(m.timestamp)}</p>
                  <p className="text-base leading-relaxed">{m.message}</p>
                </div>
              ))}
            </div>
          </ScrollArea>
          <div className="flex gap-2 pt-2">
            <Input
              value={replyMsg}
              onChange={e => setReplyMsg(e.target.value)}
              placeholder="Type a reply..."
              className="h-12 text-base"
              onKeyDown={e => { if (e.key === 'Enter') handleReply(replyDialog?.id); }}
              data-testid="reply-input"
            />
            <Button
              size="icon"
              className="h-12 w-12 bg-amber-600 hover:bg-amber-700 flex-shrink-0"
              onClick={() => handleReply(replyDialog?.id)}
              disabled={!replyMsg.trim()}
              data-testid="reply-send-btn"
            >
              <Send className="h-5 w-5 text-white" />
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ═══ KYC Step Form — Minimal, generous spacing ═══
const KYCStepForm = ({ step, kyc, customer, saving, onSave, onContactAdded, billingIsSameAsShipping, setBillingIsSameAsShipping }) => {
  const saved = kyc[`step_${step}`] || {};
  const [form, setForm] = useState(saved);

  useEffect(() => { setForm(kyc[`step_${step}`] || {}); }, [step, kyc]);

  const f = (key) => form[key] || '';
  const set = (key, val) => setForm(p => ({ ...p, [key]: val }));

  const field = (key, label, extra = {}) => (
    <div className="space-y-2">
      <Label className="text-sm font-medium">{label}</Label>
      <Input
        value={f(key)}
        onChange={e => set(key, extra.upper ? e.target.value.toUpperCase() : e.target.value)}
        placeholder={extra.placeholder}
        type={extra.type || 'text'}
        className="h-12 text-base"
        data-testid={`kyc-${key}`}
      />
    </div>
  );

  const section = (title) => (
    <p className="text-sm font-medium text-muted-foreground uppercase tracking-wider pt-2">{title}</p>
  );

  const saveBtn = (label, disabled, variant = 'amber') => (
    <Button
      className={cn(
        "w-full h-14 text-base rounded-2xl mt-6 text-white",
        variant === 'emerald' ? "bg-emerald-600 hover:bg-emerald-700" : "bg-amber-600 hover:bg-amber-700"
      )}
      onClick={() => onSave(step, step === 2 ? { ...form, shipping_same_as_billing: billingIsSameAsShipping } : form)}
      disabled={saving || disabled}
      data-testid={`kyc-save-${step}`}
    >
      {saving ? <Loader2 className="h-5 w-5 animate-spin mr-2" /> : (variant === 'emerald' ? <Check className="h-5 w-5 mr-2" /> : null)}
      {label}
      {variant !== 'emerald' && <ChevronRight className="h-5 w-5 ml-2" />}
    </Button>
  );

  if (step === 1) return (
    <div className="space-y-5">
      <h3 className="text-2xl font-semibold tracking-tight">Company details</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        {field('company_name', 'Company name')}
        {field('legal_name', 'Legal name')}
        <div className="space-y-2">
          <Label className="text-sm font-medium">Organization type</Label>
          <Select value={f('org_type')} onValueChange={v => set('org_type', v)}>
            <SelectTrigger className="h-12 text-base" data-testid="kyc-org_type"><SelectValue placeholder="Select..." /></SelectTrigger>
            <SelectContent>{ORG_TYPES.map(t => <SelectItem key={t} value={t} className="text-base">{t}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        {field('mobile', 'Mobile (WhatsApp)', { placeholder: '919876543210' })}
        <div className="sm:col-span-2">{field('email', 'Email', { type: 'email' })}</div>
      </div>
      {saveBtn('Save and continue', !f('company_name') || !f('legal_name') || !f('org_type') || !f('mobile'))}
    </div>
  );

  if (step === 2) return (
    <div className="space-y-5">
      <h3 className="text-2xl font-semibold tracking-tight">Addresses</h3>
      {section('Billing address')}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div className="sm:col-span-2">{field('billing_address_1', 'Address line 1')}</div>
        <div className="sm:col-span-2">{field('billing_address_2', 'Address line 2')}</div>
        {field('billing_city', 'City')}
        {field('billing_state', 'State')}
        {field('billing_pin', 'PIN code')}
      </div>
      <label className="flex items-center gap-3 cursor-pointer pt-3">
        <input
          type="checkbox"
          checked={billingIsSameAsShipping}
          onChange={e => setBillingIsSameAsShipping(e.target.checked)}
          className="h-5 w-5 rounded accent-amber-600"
          data-testid="shipping-same-checkbox"
        />
        <span className="text-base">Shipping address same as billing</span>
      </label>
      {!billingIsSameAsShipping && (
        <>
          {section('Shipping address')}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div className="sm:col-span-2">{field('shipping_address_1', 'Address line 1')}</div>
            <div className="sm:col-span-2">{field('shipping_address_2', 'Address line 2')}</div>
            {field('shipping_city', 'City')}
            {field('shipping_state', 'State')}
            {field('shipping_pin', 'PIN code')}
          </div>
        </>
      )}
      {saveBtn('Save and continue', !f('billing_address_1') || !f('billing_city') || !f('billing_state') || !f('billing_pin'))}
    </div>
  );

  if (step === 3) return (
    <div className="space-y-5">
      <h3 className="text-2xl font-semibold tracking-tight">Documents</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        {field('gst_number', 'GST number', { upper: true, placeholder: '22AAAAA0000A1Z5' })}
        {field('pan_number', 'PAN number', { upper: true, placeholder: 'AAAAA0000A' })}
        <div className="sm:col-span-2">{field('msme_number', 'MSME registration (optional)')}</div>
      </div>
      {saveBtn('Save and continue', !f('gst_number') || !f('pan_number'))}
    </div>
  );

  if (step === 4) return (
    <div className="space-y-5">
      <h3 className="text-2xl font-semibold tracking-tight">Banking details</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div className="sm:col-span-2">{field('account_holder', 'Account holder name')}</div>
        {field('account_number', 'Account number')}
        <div className="space-y-2">
          <Label className="text-sm font-medium">Account type</Label>
          <Select value={f('account_type')} onValueChange={v => set('account_type', v)}>
            <SelectTrigger className="h-12 text-base" data-testid="kyc-account_type"><SelectValue placeholder="Select..." /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Current" className="text-base">Current</SelectItem>
              <SelectItem value="Savings" className="text-base">Savings</SelectItem>
              <SelectItem value="OD" className="text-base">OD</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {field('ifsc', 'IFSC code', { upper: true })}
        {field('bank_name', 'Bank name')}
        {field('branch_name', 'Branch name')}
        <div className="sm:col-span-2">{field('branch_address', 'Branch address')}</div>
      </div>
      {saveBtn('Save and continue', !f('account_holder') || !f('account_number') || !f('ifsc') || !f('bank_name'))}
    </div>
  );

  if (step === 5) {
    return (
      <Step5Contacts
        customer={customer}
        saving={saving}
        onContactAdded={onContactAdded}
        onComplete={() => onSave(5, { confirmed_at: new Date().toISOString() })}
      />
    );
  }

  return null;
};

// Step 5 — Contacts list (merged with WhatsApp Numbers)
const Step5Contacts = ({ customer, saving, onContactAdded, onComplete }) => {
  const [open, setOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: '', designation: 'other', phone: '', email: '', label: '' });
  const additional = customer?.additional_phones || [];

  const handleAdd = async () => {
    if (!form.name || !form.phone) {
      toast.error('Name and WhatsApp number are required');
      return;
    }
    setAdding(true);
    try {
      await portalApi.addContact(form);
      toast.success('Contact added');
      setForm({ name: '', designation: 'other', phone: '', email: '', label: '' });
      setOpen(false);
      if (onContactAdded) onContactAdded();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to add contact');
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="space-y-2">
        <h3 className="text-2xl font-semibold tracking-tight">Contact persons</h3>
        <p className="text-base text-muted-foreground">These are the people Siddhi Jewels can reach for orders, queries and accounts.</p>
      </div>

      {/* Locked primary contact */}
      <div className="p-5 rounded-2xl border border-amber-200/60 bg-amber-50/60 dark:bg-amber-500/10 space-y-2">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <p className="text-lg font-semibold">{customer?.contact_person_name || 'Primary contact'}</p>
          <span className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full bg-amber-600 text-white">
            <Lock className="h-3 w-3" /> Login contact
          </span>
        </div>
        <p className="text-sm text-muted-foreground capitalize">{customer?.contact_designation || 'operations'}</p>
        <div className="flex items-center gap-2 text-base">
          <Phone className="h-4 w-4 text-muted-foreground" />
          <span className="font-mono">{customer?.mobile}</span>
        </div>
        {customer?.email && <p className="text-sm text-muted-foreground truncate">{customer.email}</p>}
        <p className="text-xs text-muted-foreground pt-1">
          You log in with this number, so it can't be edited from here. Contact Siddhi Jewels to update it.
        </p>
      </div>

      {/* Additional contacts */}
      {additional.length > 0 && (
        <div className="space-y-2">
          {additional.map((c, i) => (
            <div key={i} className="p-4 rounded-2xl border space-y-1">
              <div className="flex items-center justify-between gap-2">
                <p className="text-base font-medium truncate">{c.name || <span className="italic text-muted-foreground">No name</span>}</p>
                <span className="text-xs text-muted-foreground capitalize">{c.designation || 'other'}</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Phone className="h-3.5 w-3.5" />
                <span className="font-mono">{c.phone}</span>
              </div>
              {c.email && <p className="text-xs text-muted-foreground truncate">{c.email}</p>}
            </div>
          ))}
        </div>
      )}

      {/* Add new contact */}
      {open ? (
        <div className="p-5 rounded-2xl border border-amber-200/60 bg-white dark:bg-neutral-900 space-y-4">
          <p className="text-base font-semibold">Add another contact</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="sm:col-span-2 space-y-2">
              <Label className="text-sm font-medium">Name</Label>
              <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} className="h-12 text-base" data-testid="step5-name" />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium">Designation</Label>
              <Select value={form.designation} onValueChange={v => setForm(p => ({ ...p, designation: v }))}>
                <SelectTrigger className="h-12 text-base" data-testid="step5-designation"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="operations" className="text-base">Operations</SelectItem>
                  <SelectItem value="accounts" className="text-base">Accounts</SelectItem>
                  <SelectItem value="other" className="text-base">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-sm font-medium">WhatsApp number</Label>
            <Input value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} placeholder="919876543210" className="h-12 text-base font-mono" data-testid="step5-phone" />
          </div>
          <div className="space-y-2">
            <Label className="text-sm font-medium">Email</Label>
            <Input type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} className="h-12 text-base" data-testid="step5-email" />
          </div>
          <div className="flex flex-wrap gap-2">
            <Button onClick={handleAdd} disabled={adding} className="h-12 px-6 bg-amber-600 hover:bg-amber-700 text-white" data-testid="step5-save-contact">
              {adding ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />} Save contact
            </Button>
            <Button variant="outline" onClick={() => setOpen(false)} className="h-12">Cancel</Button>
          </div>
        </div>
      ) : (
        <Button variant="outline" onClick={() => setOpen(true)} className="w-full h-14 text-base rounded-2xl border-dashed" data-testid="step5-add-contact-btn">
          <Plus className="h-5 w-5 mr-2" /> Add another contact (Accounts, Manager, etc.)
        </Button>
      )}

      <Button
        className="w-full h-14 text-base rounded-2xl mt-2 bg-emerald-600 hover:bg-emerald-700 text-white"
        onClick={onComplete}
        disabled={saving}
        data-testid="kyc-save-5"
      >
        {saving ? <Loader2 className="h-5 w-5 animate-spin mr-2" /> : <Check className="h-5 w-5 mr-2" />} Complete profile
      </Button>
    </div>
  );
};

export default Portal;
